import { ChevronDownIcon, ChevronUpIcon } from '@chakra-ui/icons';
import {
  Box,
  Checkbox,
  Flex,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  IconButton,
  Input,
  Text,
  useColorModeValue,
  VStack,
} from '@chakra-ui/react';
import { Field } from 'formik';
import React, { useEffect, useRef, useState } from 'react';

export interface MultiSelectFieldProps {
  label?: string;
  options: { value: string; label: string; short?: string }[];
  isRequired?: boolean;
  isDisabled?: boolean;
  placeholder?: string;
  name: string;
  value?: string[];
  errors?: string;
  touched?: boolean;
  helpers?: any;
  setFieldValue?: (value: string[] | string) => void;
  minW?: any;
  maxW?: any;
  useShort?: boolean;
  isSingle?: boolean;
  displayValue?: boolean;
  labelFontSize?: string;
  labelMb?: string;
  tooltipElement?: React.ReactNode;
  checkboxElement?: React.ReactNode;
  addCheckbox?: {
    label: string;
    initiallyChecked?: boolean;
    clearOnDisable?: boolean;
    onToggle?: (enabled: boolean) => void;
  };
  full?: boolean;
  [x: string]: any;
}

const formatIndexWithLeadingZeros = (index: number): string => {
  return index.toString().padStart(2, '0');
};

export function MultiSelectField(props: MultiSelectFieldProps) {
  const {
    label,
    options,
    value,
    isRequired,
    isDisabled,
    errors,
    touched,
    helpers,
    setFieldValue,
    name,
    minW,
    maxW,
    placeholder,
    useShort,
    isSingle,
    displayValue,
    labelFontSize,
    labelMb,
    tooltipElement,
    checkboxElement,
    addCheckbox,
    mb,
    full,
    onDark,
    ...rest
  } = props;

  const [isOpen, setIsOpen] = useState(false);
  const [enabled, setEnabled] = useState<boolean>(
    addCheckbox ? addCheckbox.initiallyChecked ?? true : true,
  );
  const dropdownRef = useRef<HTMLDivElement | null>(null);
  const [lightColor, darkColor] = onDark
    ? ['white', 'chakra-body-text']
    : ['chakra-body-text', 'white'];

  const textColorPrimary = useColorModeValue(lightColor, darkColor);
  const textColorSecondary = useColorModeValue('chakra-body-text', 'white');

  const toggleDropdown = () => setIsOpen(!isOpen);

  const handleSelect = (option: string) => {
    if (setFieldValue) {
      if (isSingle) {
        setFieldValue(option);
        toggleDropdown();
      } else {
        const newValue = value!.includes(option)
          ? value!.filter((item) => item !== option)
          : [...value!, option];
        setFieldValue(newValue);
      }
    }
  };
  const isError = !!(errors && errors.length > 0);

  useEffect(() => {
    if (addCheckbox) {
      setEnabled(addCheckbox.initiallyChecked ?? true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addCheckbox?.initiallyChecked]);

  const effectiveDisabled = isDisabled || false || (addCheckbox ? !enabled : false);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [dropdownRef]);

  return (
    <Flex
      direction="column"
      mb={mb ?? '30px'}
      width={full ? 'full' : 'unset'}
      minW={minW}
      maxW={maxW}
    >
      <FormControl
        isDisabled={effectiveDisabled && !(checkboxElement || addCheckbox)}
        isRequired={isRequired}
        isInvalid={isError && touched}
        maxW={(!checkboxElement && !addCheckbox && maxW) || '100%'}
        color={textColorPrimary}
      >
        {label && (
          <HStack justifyContent="space-between">
            <FormLabel
              display="flex"
              fontSize={labelFontSize || '2xl'}
              fontWeight="bold"
              lineHeight="normal"
              color={textColorPrimary}
              mb={labelMb || 4}
              opacity={effectiveDisabled ? 0.4 : 1}
              _hover={{ cursor: 'pointer' }}
              htmlFor={name}
            >
              {label}
            </FormLabel>
            {tooltipElement && tooltipElement}
          </HStack>
        )}
        <Box position="relative" w="full" ref={dropdownRef}>
          {addCheckbox && (
            <Flex
              flexDir={{ base: 'column', md: 'row' }}
              gap={{ base: 2, md: 1 }}
              w="full"
              justifyContent="space-between"
            >
              <Box
                alignSelf="center"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                <Checkbox
                  colorScheme="primary"
                  isChecked={enabled}
                  isRequired={false}
                  onChange={(e) => {
                    const next = e.target.checked;
                    if (!next) {
                      setIsOpen(false);
                    }
                    setEnabled(next);
                    addCheckbox.onToggle?.(next);
                    if (!next && addCheckbox.clearOnDisable && setFieldValue) {
                      setFieldValue([]);
                    }
                  }}
                  size="lg"
                >
                  <Text fontSize="xl" whiteSpace="nowrap" color="white" mr={5}>
                    {addCheckbox.label}
                  </Text>
                </Checkbox>
              </Box>
              <Field
                as={Input}
                name={name}
                onClick={toggleDropdown}
                isDisabled={effectiveDisabled}
                value={displayValue ? value : `${formatIndexWithLeadingZeros(value!.length)}`}
                readOnly
                cursor="pointer"
                height="44px"
                placeholder={placeholder || ''}
                padding="8px"
                pl={5}
                border="2px solid"
                borderColor="gray.300"
                borderRadius="md"
                color="white"
                _hover={{ borderColor: 'gray.400' }}
                _focus={{ borderColor: 'blue.500' }}
              />
            </Flex>
          )}
          {!addCheckbox && checkboxElement && (
            <Flex
              flexDir={{ base: 'column', md: 'row' }}
              gap={{ base: 2, md: 1 }}
              w="full"
              justifyContent="space-between"
            >
              <Box
                alignSelf="center"
                onClick={(e) => e.stopPropagation()}
                onMouseDown={(e) => e.stopPropagation()}
              >
                {checkboxElement}
              </Box>
              <Field
                as={Input}
                name={name}
                onClick={toggleDropdown}
                isDisabled={effectiveDisabled}
                value={displayValue ? value : `${formatIndexWithLeadingZeros(value!.length)}`}
                readOnly
                cursor="pointer"
                height="44px"
                placeholder={placeholder || ''}
                padding="8px"
                pl={5}
                border="2px solid"
                borderColor="gray.300"
                borderRadius="md"
                color="white"
                _hover={{ borderColor: 'gray.400' }}
                _focus={{ borderColor: 'blue.500' }}
              />
            </Flex>
          )}
          {!addCheckbox && !checkboxElement && (
            <Field
              as={Input}
              name={name}
              onClick={toggleDropdown}
              isDisabled={effectiveDisabled}
              value={displayValue ? value : `${formatIndexWithLeadingZeros(value!.length)}`}
              readOnly
              cursor="pointer"
              height="44px"
              placeholder={placeholder || ''}
              padding="8px"
              pl={5}
              border="2px solid"
              borderColor="gray.300"
              borderRadius="md"
              color={textColorPrimary}
              _hover={{ borderColor: 'gray.400' }}
              _focus={{ borderColor: 'blue.500' }}
            />
          )}
          <IconButton
            icon={
              isOpen ? (
                <ChevronUpIcon w="20px" h="20px" mt={2} color="gray.600" />
              ) : (
                <ChevronDownIcon w="20px" h="20px" mt={2} color="gray.600" />
              )
            }
            onClick={toggleDropdown}
            isDisabled={effectiveDisabled}
            position="absolute"
            top="2px"
            right="6px"
            size="sm"
            variant="ghost"
            aria-label="toggle dropdown"
          />
          {isOpen && (
            <VStack
              position="absolute"
              top="46px"
              left="0"
              right="0"
              border="1px solid"
              borderColor="gray.300"
              bg="white"
              gap={0}
              zIndex="10"
              maxHeight="200px"
              overflowY="scroll"
              borderRadius="md"
              boxShadow="md"
              css={{
                '&::-webkit-scrollbar': {
                  width: '41px',
                  borderLeft: '1px solid',
                  borderColor: '#BECBD1',
                },
                '&::-webkit-scrollbar-track': {
                  background: 'transparent',
                  boxShadow: 'inset 0 0 5px black',
                  borderRadius: '4px',
                  borderLeft: '19px solid transparent',
                  borderRight: '19px solid transparent',
                },
                '&::-webkit-scrollbar-thumb': {
                  background: 'var(--chakra-colors-primary-500)',
                  borderLeft: '17px solid transparent',
                  borderRight: '17px solid transparent',
                  borderRadius: '18px',
                  backgroundClip: 'content-box',
                },
              }}
            >
              {options.map((option, index) => (
                <Box
                  key={index}
                  display="flex"
                  alignItems="center"
                  justifyContent="center"
                  textAlign="center"
                  width="100%"
                  py="8px"
                  px="46px"
                  cursor="pointer"
                  borderBottom="1px solid"
                  color={value?.includes(`${option.value}`) ? 'primaryText' : textColorSecondary}
                  borderColor="gray.300"
                  background={value?.includes(`${option.value}`) ? 'primary.400' : 'transparent'}
                  _hover={{ opacity: '0.6' }}
                  onClick={() => handleSelect(option.value)}
                >
                  {useShort && (
                    <Box
                      bg={value?.includes(option.value) ? 'primary.500' : 'gray.600'}
                      color={
                        value?.includes(`${option.value}`) ? 'primaryText' : textColorSecondary
                      }
                      position="absolute"
                      left={3}
                      px={3}
                      py={1}
                      borderRadius={4}
                      fontSize="xs"
                      fontWeight="bold"
                    >
                      {option.short}
                    </Box>
                  )}
                  {!useShort && (
                    <Text
                      position="absolute"
                      left={3}
                      color={
                        value?.includes(`${option.value}`) ? 'primaryText' : textColorSecondary
                      }
                    >
                      {formatIndexWithLeadingZeros(index + 1)}
                    </Text>
                  )}
                  <Text>{option.label}</Text>
                </Box>
              ))}
            </VStack>
          )}
        </Box>
        {isError && touched && <FormErrorMessage ml="10px">{errors}</FormErrorMessage>}
      </FormControl>
    </Flex>
  );
}
